import { env } from '../env';
import { addScript } from '../util';
import { AnalyticsEventsProps, calculateCompleteSkuName, InitCommonProps } from './analytics';

type TTQ = {
  [key in MethodsName]: (...parameters: Array<EventName | PixelId | EventData | undefined>) => void;
};

type MethodsName = 'track' | 'identify';
/* 
  | 'page'
  | 'instances'
  | 'debug'
  | 'on'
  | 'off'
  | 'once'
  | 'ready'
  | 'alias'
  | 'group'
  | 'enableCookie'
  | 'disableCookie'
  | 'holdConsent'
  | 'revokeConsent'
  | 'grantConsent' */

type EventName =
  | 'AddPaymentInfo'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'ClickButton'
  | 'CompletePayment'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Download'
  | 'FindLocation'
  | 'InitiateCheckout'
  | 'PlaceAnOrder'
  | 'Schedule'
  | 'Search'
  | 'SubmitForm'
  | 'Subscribe'
  | 'ViewContent';

type PixelId = string;

interface EventData {
  content_category?: string; //Category of the page/product.
  content_ids?: Array<string>;
  content_name?: string; //name of the product or page.
  content_type?: 'product' | 'product_group';
  contents?: Array<{
    content_id?: string;
    content_name?: string;
    content_type?: string;
    num_items?: number;
    price?: number;
    quantity?: number;
  }>;
  currency?: 'EUR';
  delivery_category?: string;
  description?: string;
  email?: string;
  external_id?: string;
  phone_number?: string;
  price?: number;
  quantity?: number;
  search_string?: string;
  status?: string;
  value?: number;
}

declare global {
  interface Window {
    ttq: TTQ;
  }
}

let initialized = false;
let debug = false;

interface InitProps extends InitCommonProps {
  id?: string;
}

export type TiktokPixelInitProps = InitProps;

const init = (props?: InitProps): void => {
  const id = props?.id ?? env.REACT_APP_TIKTOK_PIXEL_ID;
  debug = props?.debug || false;
  if (id) {
    if (!initialized) {
      initialized = true;
      install(id);
    }
  }
};

const install = (pixelId: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  window.ttq = window.ttq || [];

  addScript({
    id: 'tiktok-pixel',
    text: `
    !function (w, d, t) {
    w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
    var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
    ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
    ttq.load('${pixelId}');
    ttq.page();
    }(window, document, 'ttq');`,
  });
};

const ttq: TTQ = {
  identify: (data) => {
    if (initialized) {
      log('identify', data);
      window.ttq.identify(data);
    } else {
      log('Blocked because not initialized.', 'identify', data);
    }
  },
  track: (eventName, data) => {
    if (initialized) {
      log('track', eventName, data);
      window.ttq.track(eventName, data);
    } else {
      log('Blocked because not initialized.', eventName, data);
    }
  },
};

const log = (message?: unknown, ...optionalParams: Array<unknown>) => {
  if (debug) {
    console.log('analytics', 'tiktok:', message, ...optionalParams);
  }
};

export const tiktokPixel: {
  events: AnalyticsEventsProps;
  init(props?: InitProps): void;
} = {
  events: {
    addPaymentInfo: (props) => {
      ttq.track('AddPaymentInfo', {
        contents: props.products.map((product) => ({
          content_id: product.sku.id,
          content_name: calculateCompleteSkuName(product.name, product.sku.name),
          content_type: 'product',
          num_items: product.quantity,
        })),
        currency: 'EUR',
        value: props.totalCost,
      });
    },
    addShippingInfo: (props) => {
      // Assente in TikTok
    },
    addToCart: (props) => {
      ttq.track('AddToCart', {
        contents: props.products.map((product) => ({
          content_id: product.sku.id,
          content_name: calculateCompleteSkuName(product.name, product.sku.name),
          content_type: 'product',
          num_items: product.quantity,
        })),
        currency: 'EUR',
        value: props.totalPrice.value,
      });
    },
    addToWishlist: (props) => {
      // Assente in TikTok
    },
    checkoutInit: (props) => {
      ttq.track('InitiateCheckout', {
        contents: props.products.map((product) => ({
          content_id: product.sku.id,
          content_name: calculateCompleteSkuName(product.name, product.sku.name),
          content_type: 'product',
          num_items: product.quantity,
        })),
        currency: 'EUR',
        value: props.totalCost,
      });
    },
    closePopup: (props) => {
      ttq.track('ClickButton', {
        contents: [
          {
            content_id: props.popupId,
            content_name: props.popupCode,
            content_type: 'popup',
          },
        ],
        description: 'popup_close',
      });
    },
    completeRegistration: (props) => {
      ttq.track('CompleteRegistration', {
        content_name: props.origin,
      });
    },
    contact: (props) => {
      // Assente in TikTok
    },
    customizeProduct: (props) => {
      // Assente in TikTok
    },
    donate: (props) => {
      // Assente in TikTok
    },
    exception: (props) => {
      // Assente in TikTok
    },
    findLocation: (props) => {
      // Assente in TikTok
    },
    lead: (props) => {
      // Assente in TikTok
    },
    login: (props) => {
      // Assente in TikTok
    },
    maximizePopup: (props) => {
      ttq.track('ClickButton', {
        contents: [
          {
            content_id: props.popupId,
            content_name: props.popupCode,
            content_type: 'popup',
          },
        ],
        description: 'popup_restore',
      });
    },
    minimizePopup: (props) => {
      ttq.track('ClickButton', {
        contents: [
          {
            content_id: props.popupId,
            content_name: props.popupCode,
            content_type: 'popup',
          },
        ],
        description: 'popup_minimize',
      });
    },
    pageInformation: (props) => {
      // Assente in TikTok
    },
    pageLeave: (props) => {
      // Assente in TikTok
    },
    pageView: (props) => {
      // Assente in TikTok
    },
    purchase: (props) => {
      ttq.track('CompletePayment', {
        contents: props.products.map((product) => ({
          content_id: product.sku.id,
          content_name: calculateCompleteSkuName(product.name, product.sku.name),
          content_type: 'product',
          num_items: product.quantity,
        })),
        currency: 'EUR',
        //description: , // string. Non-hashed public IP address of the browser.
        value: props.totalCost,
      });
    },
    removeFromCart: (props) => {
      // Assente in TikTok
    },
    schedule: (props) => {
      // Assente in TikTok
    },
    search: (props) => {
      ttq.track('Search', { search_string: props.simpleSearch });
    },
    selectProduct: (props) => {
      // Assente in TikTok
    },
    startPayment: (props) => {
      // Assente in TikTok
    },
    submitApplication: (props) => {
      // Assente in TikTok
    },
    viewCart: (props) => {
      // Assente in TikTok
    },
    viewPopup: (props) => {
      ttq.track('ViewContent', {
        contents: [
          {
            content_id: props.popupId,
            content_name: props.popupCode,
            content_type: 'popup',
          },
        ],
        description: 'popup_view',
      });
    },
    viewProduct: (props) => {
      ttq.track('ViewContent', {
        contents: [
          {
            content_id: props.product.skus[0].id,
            content_name: props.product.name,
            content_type: 'product',
          },
        ],
      });
    },
    viewProducts: (props) => {
      // Assente in TikTok
    },
  },
  init: (props) => {
    init(props);
  },
};
